import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';

import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import qs from 'qs';

import { Row, Col } from 'antd';
import {
  InfoCircleOutlined,
  LeftCircleTwoTone,
  RightCircleTwoTone,
} from '@ant-design/icons';

import moment from 'moment-timezone';

import { format } from '../../utils/index';
import { MixType } from '../../utils/types';
import { mixSelector, loadAllMix } from '../../store/mix';
import {
  reactorSetIndicatorsSelector,
  loadAllReactors,
} from '../../store/reactors';
import { getCurrentDate } from '../../store/otherSelectors';
import { loadAllProductions } from '../../store/productions';
import { loadAllUnavailabilities } from '../../store/unavailabilities';

import './index.css';
import MixComponent from './components/MixComponent';

// Create stable selectors
const selectMixForDate = createSelector(
  [state => state, (state, currentDate) => currentDate],
  (state, currentDate) => mixSelector({ date: currentDate }, state),
);

const selectReactorSetIndicators = createSelector(
  [
    state => state,
    (state, currentDate) => currentDate,
    (state, currentDate, slotIndex) => slotIndex,
  ],
  (state, currentDate, slotIndex) => {
    const hourIndex = Math.floor((slotIndex ?? 0) / 4);
    return reactorSetIndicatorsSelector(
      { date: currentDate, slotIndex: hourIndex },
      state,
    );
  },
);

function MixView() {
  const location = useLocation();
  const dispatch = useDispatch();
  const [currentDate, setCurrentDate] = useState(getCurrentDate(location));

  const mix = useSelector(state => selectMixForDate(state, currentDate)) || [];
  // Defensive: if mix is not an array, fallback to empty array
  const safeMix = Array.isArray(mix) ? mix : [];

  const initialSlotIndex = useMemo(() => {
    if (!safeMix.length) return 0;
    return moment().isSame(currentDate, 'day')
      ? safeMix.filter(d => !Number.isNaN(d.nuclear)).length - 1
      : moment(currentDate).hours() * 4;
  }, [currentDate, safeMix]);

  const [slotIndex, setSlotIndex] = useState(initialSlotIndex);

  useEffect(() => {
    setSlotIndex(initialSlotIndex);
  }, [initialSlotIndex]);

  // Defensive: fallback for slotIndex out of bounds
  const currentMix = safeMix[slotIndex] || {};

  const reactorSetIndicators =
    useSelector(state =>
      selectReactorSetIndicators(state, currentDate, slotIndex),
    ) || {};

  const {
    totalPower = 0,
    availablePower = 0,
    availableCount = 0,
    partiallyUnavailableCount,
    totallyUnavailableCount,
  } = reactorSetIndicators;

  const reactorData =
    useSelector(state =>
      selectReactorSetIndicators(state, currentDate, slotIndex),
    ) || {};

  console.log('Reactor data:', reactorData);
  console.log('Current mix:', currentMix);
  console.log('Current slot index:', slotIndex);
  console.log('Current date:', currentDate);
  console.log('Total power:', totalPower);
  console.log('Available power:', availablePower);
  console.log('Available count:', availableCount);
  console.log('Partially unavailable count:', partiallyUnavailableCount);
  console.log('Totally unavailable count:', totallyUnavailableCount);

  const total = totalPower ? Math.floor(totalPower / 1000) : 0;
  console.log(`Total: ${total}`);
  const available = Math.floor(availablePower);
  const availabilityRate = totalPower
    ? Math.floor((100 * availablePower) / totalPower)
    : 0;
  const prodAvailableRate = availablePower
    ? Math.floor((100 * (currentMix.nuclear || 0)) / availablePower)
    : 0;
  const availableSize = totalPower
    ? Math.max(
        ((availablePower - (currentMix.nuclear || 0)) / totalPower) * 100,
        0,
      )
    : 0;
  const date = moment(currentDate)
    .hour(Math.floor(slotIndex / 4))
    .minutes(15 * (slotIndex % 4))
    .format('DD/MM/YYYY HH:mm');

  useEffect(() => {
    // Needed for totalPower and availability computations
    dispatch(loadAllReactors());
  }, [dispatch]);

  useEffect(() => {
    dispatch(loadAllMix({ date: currentDate }));
    dispatch(loadAllProductions({ date: currentDate }));
    dispatch(loadAllUnavailabilities({ date: currentDate }));
  }, [currentDate, dispatch]);

  return (
    <div className="MixView">
      <Row>
        <Col span={24}>
          <MixComponent
            mix={safeMix}
            slotIndex={slotIndex}
            setSlotIndex={setSlotIndex}
            currentDate={currentDate}
          />
          <div className="MixView__date">
            {moment(currentDate).isAfter('2012-01-02') ? (
              <div
                onClick={() =>
                  setCurrentDate(
                    moment(currentDate)
                      .subtract(1, 'day')
                      .startOf('day')
                      .toISOString(),
                  )
                }
              >
                <LeftCircleTwoTone style={{ fontSize: '18pt' }} />
              </div>
            ) : (
              <div />
            )}
            <div>{date}</div>
            {!moment().isSame(currentDate, 'day') ? (
              <div
                onClick={() =>
                  setCurrentDate(
                    moment(currentDate)
                      .add(1, 'day')
                      .startOf('day')
                      .toISOString(),
                  )
                }
              >
                <RightCircleTwoTone style={{ fontSize: '18pt' }} />
              </div>
            ) : (
              <div />
            )}
          </div>
          <div className="MixView__nuclear">
            <strong>{`Parc nucléaire (${total} GW):`}</strong>
            <div className="MixView__nuclear__indicator">
              <div
                className="MixView__nuclear__indicator__prod"
                style={{
                  display: currentMix.nuclear ? 'inherit' : 'none',
                  width: `${((currentMix.nuclear || 0) / (totalPower || 1)) *
                    100}%`,
                }}
              >
                <div>{`Prod: ${format(currentMix.nuclear || 0)} MW`}</div>
                <div>{`${format(prodAvailableRate)}% du dispo.`}</div>
              </div>
              <div
                className="MixView__nuclear__indicator__available"
                style={{
                  width: `${availableSize}%`,
                }}
              />
            </div>
            <div className="MixView__nuclear__available">
              {`${availableCount} réacteurs disponibles: ${available} MW (${availabilityRate}%)`}
            </div>
            <div>
              {`dont ${partiallyUnavailableCount} partiellement indisponible${
                partiallyUnavailableCount > 1 ? 's' : ''
              }`}
            </div>
            <div className="MixView__nuclear__stopped">
              {`${totallyUnavailableCount} réacteur${
                totallyUnavailableCount > 1 ? 's' : ''
              } arrêté${totallyUnavailableCount > 1 ? 's' : ''}`}
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
}

export default MixView;
